import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import {
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormHelperText,
    IconButton,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import MaterialTable, { MaterialTableProps } from 'material-table';
import Moment from 'moment';
import { toast } from 'react-toastify';
import DocumentTypeEntity from '../../../entities/DocumentType.Entity';
import ReferralDocumentEntity from '../../../entities/ReferralDocument.Entity';
import {
    getRtwValidationToastMessage,
    isRtwFormValid,
    validateRtwFields,
} from '../../referral-components/referral-add/referral-service/RtwFieldValidation';
import {
    getRtwAdditionalCommentsPlaceholder,
    RTW_HOURS_PER_WEEK_FIELD_LABEL,
    RTW_LIGHT_DUTY_LOCATION_OPTIONS,
    RTW_UNION_MEMBER_OPTIONS,
} from '../../referral-components/referral-add/referral-service/RtwServiceConstants';
import attachStyles from '../attach-documents-submit-modal/AttachDocumentsSubmitModal.Styles';
import rtwStyles from './RtwAdditionalInfoSubmitModal.Styles';

const ADDITIONAL_COMMENTS_MAX = 2500;

export interface RtwSubmitReferralPayload {
    documents: Array<ReferralDocumentEntity>;
    rushRequest: boolean;
    additionalComments: string;
    physicalRestrictions: string;
    primaryLanguage: string;
    hourlyRate: string;
    isUnionMember: string;
    requiresAlternateHoursEnabled: boolean;
    transitionHoursPerWeek: string;
    requiresAlternateScheduleEnabled: boolean;
    workSchedulePreferences: string;
    lightDutyLocation: string;
}

interface IRtwAdditionalInfoSubmitModalProps {
    open: boolean;
    loading: boolean;
    documentTypes: Array<DocumentTypeEntity>;
    availableDocuments: Array<ReferralDocumentEntity>;
    onClose: () => void;
    onLoadDocumentTypes: () => void;
    onSearchDocuments: (
        selectedDocumentTypeIds: Array<number>,
        dateFrom: Date | null,
        dateTo: Date | null,
    ) => void;
    onSubmitWithDocuments: (payload: RtwSubmitReferralPayload) => void;
    selectedRtwServiceTypeId?: number;
}

const TableContainer: React.FunctionComponent = ({ children }) => <>{children}</>;

export const RtwAdditionalInfoSubmitModal: FunctionComponent<IRtwAdditionalInfoSubmitModalProps> = (
    props: IRtwAdditionalInfoSubmitModalProps,
) => {
    const { onLoadDocumentTypes, open } = props;
    const classes = attachStyles();
    const rtwClasses = rtwStyles();

    const [selectedDocumentTypeIds, setSelectedDocumentTypeIds] = useState<Array<number>>([]);
    const [selectedDocuments, setSelectedDocuments] = useState<Array<ReferralDocumentEntity>>([]);
    const [rushRequest, setRushRequest] = useState(false);
    const [additionalComments, setAdditionalComments] = useState('');
    const [physicalRestrictions, setPhysicalRestrictions] = useState('');
    const [primaryLanguage, setPrimaryLanguage] = useState('');
    const [hourlyRate, setHourlyRate] = useState('');
    const [isUnionMember, setIsUnionMember] = useState('');
    const [requiresAlternateHoursEnabled, setRequiresAlternateHoursEnabled] = useState(false);
    const [transitionHoursPerWeek, setTransitionHoursPerWeek] = useState('');
    const [requiresAlternateScheduleEnabled, setRequiresAlternateScheduleEnabled] = useState(false);
    const [workSchedulePreferences, setWorkSchedulePreferences] = useState('');
    const [lightDutyLocation, setLightDutyLocation] = useState('');
    const [dateRangeFrom, setDateRangeFrom] = useState('');
    const [dateRangeTo, setDateRangeTo] = useState('');
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const directOpenLoadRequestedRef = useRef(false);

    const fieldValidationState = useMemo(
        () => ({
            physicalRestrictions,
            primaryLanguage,
            hourlyRate,
            isUnionMember,
            lightDutyLocation,
            requiresAlternateHoursEnabled,
            transitionHoursPerWeek,
            requiresAlternateScheduleEnabled,
            workSchedulePreferences,
        }),
        [
            physicalRestrictions,
            primaryLanguage,
            hourlyRate,
            isUnionMember,
            lightDutyLocation,
            requiresAlternateHoursEnabled,
            transitionHoursPerWeek,
            requiresAlternateScheduleEnabled,
            workSchedulePreferences,
        ],
    );

    const fieldErrors = useMemo(() => validateRtwFields(fieldValidationState), [fieldValidationState]);

    const resetForm = () => {
        setShowValidationErrors(false);
        setSelectedDocumentTypeIds([]);
        setSelectedDocuments([]);
        setRushRequest(false);
        setAdditionalComments('');
        setPhysicalRestrictions('');
        setPrimaryLanguage('');
        setHourlyRate('');
        setIsUnionMember('');
        setRequiresAlternateHoursEnabled(false);
        setTransitionHoursPerWeek('');
        setRequiresAlternateScheduleEnabled(false);
        setWorkSchedulePreferences('');
        setLightDutyLocation('');
        setDateRangeFrom('');
        setDateRangeTo('');
    };

    useEffect(() => {
        if (!open) {
            resetForm();
            directOpenLoadRequestedRef.current = false;
            return;
        }
        if (!directOpenLoadRequestedRef.current) {
            directOpenLoadRequestedRef.current = true;
            onLoadDocumentTypes();
        }
    }, [open, onLoadDocumentTypes]);

    const orderedDocumentTypes = useMemo(
        () =>
            [...(props.documentTypes ?? [])].sort(
                (a, b) => (a.orderToDisplay ?? Number.MAX_SAFE_INTEGER) - (b.orderToDisplay ?? Number.MAX_SAFE_INTEGER),
            ),
        [props.documentTypes],
    );

    const dateFilterAnchorDocumentTypeId = useMemo(() => {
        const selectedOfGroup = orderedDocumentTypes.filter(
            (t) => !!t.isDateFilterRequired && selectedDocumentTypeIds.includes(t.documentTypeId),
        );
        if (selectedOfGroup.length === 0) {
            return null;
        }
        return selectedOfGroup[selectedOfGroup.length - 1].documentTypeId;
    }, [orderedDocumentTypes, selectedDocumentTypeIds]);

    const selectionRequiresDateFilter = useMemo(
        () =>
            orderedDocumentTypes.some(
                (t) => !!t.isDateFilterRequired && selectedDocumentTypeIds.includes(t.documentTypeId),
            ),
        [orderedDocumentTypes, selectedDocumentTypeIds],
    );

    useEffect(() => {
        if (dateFilterAnchorDocumentTypeId == null) {
            setDateRangeFrom('');
            setDateRangeTo('');
        }
    }, [dateFilterAnchorDocumentTypeId]);

    const selectedDocumentNumberSet = useMemo(
        () => new Set((selectedDocuments ?? []).map((d) => d.documentNumber)),
        [selectedDocuments],
    );

    const tableData = useMemo(
        () =>
            (props.availableDocuments ?? []).map((doc) => ({
                ...doc,
                tableData: {
                    ...(doc as any).tableData,
                    checked: selectedDocumentNumberSet.has(doc.documentNumber),
                },
            })),
        [props.availableDocuments, selectedDocumentNumberSet],
    );

    const handleClose = () => {
        resetForm();
        props.onClose();
    };

    const handleSelectAll = (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
        const allIds = checked ? orderedDocumentTypes.map((type) => type.documentTypeId) : [];
        setSelectedDocumentTypeIds(allIds);
        if (!checked) {
            setSelectedDocuments([]);
        }
    };

    const handleSelectDocumentType = (documentTypeId: number, checked: boolean) => {
        const updatedIds = checked
            ? [...selectedDocumentTypeIds, documentTypeId]
            : selectedDocumentTypeIds.filter((id) => id !== documentTypeId);

        setSelectedDocumentTypeIds(updatedIds);

        if (updatedIds.length === 0) {
            setSelectedDocuments([]);
            return;
        }

        setSelectedDocuments((previousSelected) =>
            previousSelected.filter((document) => updatedIds.includes(document.documentTypeId)),
        );
    };

    const handleSearchDocuments = () => {
        let dateFrom: Date | null = null;
        let dateTo: Date | null = null;
        if (selectionRequiresDateFilter) {
            if (dateRangeFrom) {
                dateFrom = new Date(`${dateRangeFrom}T00:00:00`);
            }
            if (dateRangeTo) {
                dateTo = new Date(`${dateRangeTo}T00:00:00`);
            }
        }
        props.onSearchDocuments(selectedDocumentTypeIds, dateFrom, dateTo);
    };

    const handleSubmit = () => {
        const errors = validateRtwFields(fieldValidationState);

        if (!isRtwFormValid(errors)) {
            setShowValidationErrors(true);
            const toastMessage = getRtwValidationToastMessage(errors);
            toast.warning(`⚠️ ${toastMessage}`);
            return;
        }

        props.onSubmitWithDocuments({
            documents: selectedDocuments,
            rushRequest,
            additionalComments,
            physicalRestrictions: physicalRestrictions.trim(),
            primaryLanguage: primaryLanguage.trim(),
            hourlyRate: hourlyRate.trim(),
            isUnionMember,
            requiresAlternateHoursEnabled,
            transitionHoursPerWeek: requiresAlternateHoursEnabled ? transitionHoursPerWeek.trim() : '',
            requiresAlternateScheduleEnabled,
            workSchedulePreferences: requiresAlternateScheduleEnabled ? workSchedulePreferences.trim() : '',
            lightDutyLocation,
        });
    };

    const renderDateRangeFields = (currentDocumentTypeId: number) => {
        if (currentDocumentTypeId !== dateFilterAnchorDocumentTypeId) {
            return null;
        }

        return (
            <div className={classes.dateRangeBlock}>
                <div className={classes.dateRangeFieldWrap}>
                    <Typography component="label" htmlFor="rtw-attach-docs-date-from" className={classes.dateRangeFieldLabel}>
                        Date Range From
                    </Typography>
                    <TextField
                        id="rtw-attach-docs-date-from"
                        className={classes.dateRangeInput}
                        type="date"
                        value={dateRangeFrom}
                        onChange={(e) => setDateRangeFrom(e.target.value)}
                        disabled={props.loading}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
                <div className={classes.dateRangeFieldWrap}>
                    <Typography component="label" htmlFor="rtw-attach-docs-date-to" className={classes.dateRangeFieldLabel}>
                        To
                    </Typography>
                    <TextField
                        id="rtw-attach-docs-date-to"
                        className={classes.dateRangeInput}
                        type="date"
                        value={dateRangeTo}
                        onChange={(e) => setDateRangeTo(e.target.value)}
                        disabled={props.loading}
                        variant="outlined"
                        size="small"
                        fullWidth
                    />
                </div>
            </div>
        );
    };

    const tableProps: MaterialTableProps<ReferralDocumentEntity> = {
        columns: [
            { title: 'Description', field: 'fileDescription' },
            { title: 'File Name', field: 'fileName' },
            { title: 'Document Type', field: 'documentType' },
            {
                title: 'Date',
                render: (rowData) => (rowData.dueDate ? Moment(rowData.dueDate).format('M/D/YYYY') : ''),
            },
        ],
        data: tableData,
        options: {
            selection: true,
            paging: false,
            toolbar: false,
            maxBodyHeight: 220,
            minBodyHeight: 220,
            headerStyle: {
                paddingTop: 0,
                paddingBottom: 0,
                height: 16,
                minHeight: 16,
                fontSize: '13px',
                lineHeight: '14px',
            },
            selectionProps: (rowData: ReferralDocumentEntity) => ({
                disabled: !selectedDocumentTypeIds.includes(rowData.documentTypeId),
                color: 'secondary',
            }),
            rowStyle: (rowData: ReferralDocumentEntity) =>
                selectedDocumentTypeIds.includes(rowData.documentTypeId) ? undefined : { opacity: 0.6 },
        },
        onSelectionChange: (rows) =>
            setSelectedDocuments(
                (rows ?? []).filter((d) => selectedDocumentTypeIds.includes(d.documentTypeId)),
            ),
    };

    const isAllSelected =
        orderedDocumentTypes.length > 0 && selectedDocumentTypeIds.length === orderedDocumentTypes.length;

    const additionalCommentsPlaceholder = getRtwAdditionalCommentsPlaceholder(props.selectedRtwServiceTypeId);

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ style: { borderRadius: 4 } }}
        >
            <DialogTitle className={classes.siuExpandedTitle} disableTypography>
                <Typography component="span" variant="h6" className={classes.siuExpandedTitleText}>
                    Additional Information and Documents
                </Typography>
                <IconButton aria-label="close" className={classes.siuExpandedCloseButton} onClick={handleClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent className={classes.expandedDialogContent}>
                <FormControlLabel
                    className={classes.rushRow}
                    control={
                        <Checkbox
                            color="secondary"
                            checked={rushRequest}
                            onChange={(_e, c) => setRushRequest(c)}
                            disabled={props.loading}
                        />
                    }
                    label="This is a Rush Request"
                />

                <div className={rtwClasses.rtwFieldsBox}>
                    <div className={rtwClasses.rtwField}>
                        <Typography className={rtwClasses.rtwFieldLabel}>
                            Current Physical Restrictions provided by a treating physician{' '}
                            <span className={rtwClasses.rtwRequiredMark}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={physicalRestrictions}
                            onChange={(e) => setPhysicalRestrictions(e.target.value)}
                            disabled={props.loading}
                            error={showValidationErrors && !!fieldErrors.physicalRestrictions}
                            helperText={showValidationErrors ? fieldErrors.physicalRestrictions ?? ' ' : ' '}
                            FormHelperTextProps={{ style: { marginTop: 4 } }}
                        />
                    </div>

                    <div className={rtwClasses.rtwField}>
                        <Typography className={rtwClasses.rtwFieldLabel}>
                            Primary Language <span className={rtwClasses.rtwRequiredMark}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={primaryLanguage}
                            onChange={(e) => setPrimaryLanguage(e.target.value)}
                            disabled={props.loading}
                            error={showValidationErrors && !!fieldErrors.primaryLanguage}
                            helperText={showValidationErrors ? fieldErrors.primaryLanguage ?? ' ' : ' '}
                            FormHelperTextProps={{ style: { marginTop: 4 } }}
                        />
                    </div>

                    <div className={rtwClasses.rtwField}>
                        <Typography className={rtwClasses.rtwFieldLabel}>
                            Hourly Rate of Pay Employer will Pay while in Return to Work{' '}
                            <span className={rtwClasses.rtwRequiredMark}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            value={hourlyRate}
                            onChange={(e) => setHourlyRate(e.target.value)}
                            disabled={props.loading}
                            error={showValidationErrors && !!fieldErrors.hourlyRate}
                            helperText={showValidationErrors ? fieldErrors.hourlyRate ?? ' ' : ' '}
                            FormHelperTextProps={{ style: { marginTop: 4 } }}
                        />
                    </div>

                    <div className={rtwClasses.rtwField}>
                        <Typography className={rtwClasses.rtwFieldLabel}>
                            Is the Injured Worker a Union Member? <span className={rtwClasses.rtwRequiredMark}>*</span>
                        </Typography>
                        <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={showValidationErrors && !!fieldErrors.isUnionMember}
                        >
                            <Select
                                className={rtwClasses.rtwSelect}
                                value={isUnionMember}
                                onChange={(e) => setIsUnionMember(e.target.value as string)}
                                displayEmpty
                                disabled={props.loading}
                            >
                                <MenuItem value="">
                                    <em>Select</em>
                                </MenuItem>
                                {RTW_UNION_MEMBER_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {showValidationErrors && fieldErrors.isUnionMember && (
                                <FormHelperText>{fieldErrors.isUnionMember}</FormHelperText>
                            )}
                        </FormControl>
                    </div>

                    <FormControlLabel
                        className={rtwClasses.rtwCheckboxLabel}
                        control={
                            <Checkbox
                                color="secondary"
                                checked={requiresAlternateHoursEnabled}
                                onChange={(_e, checked) => {
                                    setRequiresAlternateHoursEnabled(checked);
                                    if (!checked) {
                                        setTransitionHoursPerWeek('');
                                    }
                                }}
                                disabled={props.loading}
                            />
                        }
                        label="Assignments are typically 32–40 hours per week. For alternate hours, check here."
                    />
                    {requiresAlternateHoursEnabled && (
                        <div className={rtwClasses.rtwField}>
                            <Typography className={rtwClasses.rtwFieldLabel}>
                                {RTW_HOURS_PER_WEEK_FIELD_LABEL}{' '}
                                <span className={rtwClasses.rtwRequiredMark}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={transitionHoursPerWeek}
                                onChange={(e) => setTransitionHoursPerWeek(e.target.value)}
                                disabled={props.loading}
                                error={showValidationErrors && !!fieldErrors.transitionHoursPerWeek}
                                helperText={showValidationErrors ? fieldErrors.transitionHoursPerWeek ?? ' ' : ' '}
                                FormHelperTextProps={{ style: { marginTop: 4 } }}
                            />
                        </div>
                    )}

                    <FormControlLabel
                        className={rtwClasses.rtwCheckboxLabel}
                        control={
                            <Checkbox
                                color="secondary"
                                checked={requiresAlternateScheduleEnabled}
                                onChange={(_e, checked) => {
                                    setRequiresAlternateScheduleEnabled(checked);
                                    if (!checked) {
                                        setWorkSchedulePreferences('');
                                    }
                                }}
                                disabled={props.loading}
                            />
                        }
                        label="Assignments are typically between 7A-7P, Mon–Sat. For an alternate schedule, check here."
                    />
                    {requiresAlternateScheduleEnabled && (
                        <div className={rtwClasses.rtwField}>
                            <Typography className={rtwClasses.rtwFieldLabel}>
                                Work Schedule Preferences <span className={rtwClasses.rtwRequiredMark}>*</span>
                            </Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                value={workSchedulePreferences}
                                onChange={(e) => setWorkSchedulePreferences(e.target.value)}
                                disabled={props.loading}
                                error={showValidationErrors && !!fieldErrors.workSchedulePreferences}
                                helperText={showValidationErrors ? fieldErrors.workSchedulePreferences ?? ' ' : ' '}
                                FormHelperTextProps={{ style: { marginTop: 4 } }}
                            />
                        </div>
                    )}

                    <div className={rtwClasses.rtwField}>
                        <Typography className={rtwClasses.rtwFieldLabel}>
                            Which location would you like the injured worker to work light duty?{' '}
                            <span className={rtwClasses.rtwRequiredMark}>*</span>
                        </Typography>
                        <FormControl
                            variant="outlined"
                            size="small"
                            fullWidth
                            error={showValidationErrors && !!fieldErrors.lightDutyLocation}
                        >
                            <Select
                                className={rtwClasses.rtwSelect}
                                value={lightDutyLocation}
                                onChange={(e) => setLightDutyLocation(e.target.value as string)}
                                displayEmpty
                                disabled={props.loading}
                            >
                                <MenuItem value="">
                                    <em>Select</em>
                                </MenuItem>
                                {RTW_LIGHT_DUTY_LOCATION_OPTIONS.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {option}
                                    </MenuItem>
                                ))}
                            </Select>
                            {showValidationErrors && fieldErrors.lightDutyLocation && (
                                <FormHelperText>{fieldErrors.lightDutyLocation}</FormHelperText>
                            )}
                        </FormControl>
                    </div>
                </div>

                <Typography variant="subtitle1" className={classes.sectionTitleTeal}>
                    Additional Comments
                </Typography>
                <div className={classes.commentsFieldWrap}>
                    <TextField
                        multiline
                        rows={5}
                        fullWidth
                        variant="outlined"
                        value={additionalComments}
                        onChange={(e) => {
                            const next = e.target.value;
                            if (next.length <= ADDITIONAL_COMMENTS_MAX) {
                                setAdditionalComments(next);
                            }
                        }}
                        disabled={props.loading}
                        inputProps={{ maxLength: ADDITIONAL_COMMENTS_MAX }}
                        className={classes.commentsTextField}
                        placeholder={additionalCommentsPlaceholder}
                    />
                    <Typography variant="caption" className={classes.charCounter}>
                        {additionalComments.length} / {ADDITIONAL_COMMENTS_MAX}
                    </Typography>
                </div>

                <div className={classes.documentsSectionHeader}>
                    <Typography variant="subtitle1" className={classes.documentsSectionTitle}>
                        Documents
                    </Typography>
                </div>

                <FormControlLabel
                    className={`${classes.filterLabel} ${classes.selectAllLabel}`}
                    control={
                        <Checkbox
                            color="secondary"
                            checked={isAllSelected}
                            onChange={handleSelectAll}
                            disabled={props.loading || orderedDocumentTypes.length === 0}
                        />
                    }
                    label="Select All"
                />

                <div className={classes.documentTypesColumnFlow}>
                    {orderedDocumentTypes.map((documentType) => (
                        <div key={documentType.documentTypeId} className={classes.documentTypesColumnItem}>
                            <FormControlLabel
                                className={`${classes.filterLabel} ${classes.documentTypeLabel}`}
                                control={
                                    <Checkbox
                                        color="secondary"
                                        checked={selectedDocumentTypeIds.includes(documentType.documentTypeId)}
                                        onChange={(_event, checked) =>
                                            handleSelectDocumentType(documentType.documentTypeId, checked)
                                        }
                                        disabled={props.loading}
                                    />
                                }
                                label={documentType.documentTypeName}
                            />
                            {renderDateRangeFields(documentType.documentTypeId)}
                        </div>
                    ))}
                </div>

                <div className={classes.searchDocumentsRow}>
                    <Button
                        variant="outlined"
                        className={`${classes.secondaryButton} ${classes.searchDocumentsButton}`}
                        onClick={handleSearchDocuments}
                        disabled={
                            props.loading ||
                            orderedDocumentTypes.length === 0 ||
                            selectedDocumentTypeIds.length === 0
                        }
                    >
                        Search Documents
                    </Button>
                </div>

                <Typography variant="subtitle2" className={classes.tableTitle}>
                    Available Documents
                </Typography>

                <div className={classes.tableContainer}>
                    {props.loading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <MaterialTable
                            components={{
                                Container: TableContainer,
                            }}
                            {...tableProps}
                        />
                    )}
                </div>
            </DialogContent>

            <DialogActions className={classes.footerActionsBar}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    className={classes.secondaryButton}
                    disabled={props.loading}
                >
                    Back
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    color="primary"
                    className={classes.primaryButton}
                    disabled={props.loading}
                >
                    Submit Referral
                </Button>
            </DialogActions>
        </Dialog>
    );
};
